<template>
  <ActivityDialog :model-value="Boolean(invitationModal)" :title="activityTitle" class="invitation-backdrop" @update:model-value="close">
    <div class="invitation-content">
      <p class="invitation-intro">分享专属链接，查看好友的邀请进度与奖励。</p>
      <img v-if="heroImage && !heroImageFailed" class="invitation-banner" :src="heroImage" :alt="activityTitle" @error="heroImageFailed = true" />

      <dl class="invitation-summary" aria-label="我的邀请统计">
        <div><dt>邀请注册</dt><dd>{{ invitationModal?.registeredCount ?? 0 }}<span>人</span></dd></div>
        <div><dt>邀请成功</dt><dd>{{ invitationModal?.qualifiedCount ?? invitationModal?.successfulCount ?? 0 }}<span>人</span></dd></div>
        <div class="reward"><dt>累计奖励</dt><dd>{{ formatAmount(invitationModal?.rewardAmount) }}<span>元</span></dd></div>
      </dl>

      <section class="invitation-share" aria-label="我的邀请链接">
        <div class="invitation-share-heading">
          <div><span>我的邀请码</span><b>{{ invitationModal?.invitationCode || '--' }}</b></div>
          <button v-if="invitationLink" type="button" class="invitation-copy" @click="copyInvitationLink"><SiteIcon name="receipt" :size="16" />复制邀请链接</button>
        </div>
        <input v-if="invitationLink" class="invitation-link" :value="invitationLink" readonly aria-label="我的邀请链接" @focus="$event.target.select()" />
      </section>

      <section class="invitation-rules" aria-labelledby="invitation-rules-title">
        <div class="invitation-section-title"><h3 id="invitation-rules-title">奖励规则</h3><span v-if="invitationRewardRules.length">以实际达标记录为准</span></div>
        <div v-if="invitationRewardRules.length" class="invitation-rule-list">
          <article v-for="(rule, index) in invitationRewardRules" :key="rule.type">
            <span class="invitation-rule-number" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span>
            <div><h4>{{ rule.title }}</h4><p>{{ rule.condition }}</p><span class="invitation-rule-result">{{ rule.result }}</span></div>
          </article>
        </div>
        <div v-else class="invitation-empty" role="status"><b>暂无可参与的奖励</b><p>奖励开启后，可在这里查看参与条件和奖励金额。</p></div>
      </section>

      <details class="invitation-notes">
        <summary>奖励发放说明<SiteIcon name="caret-down" :size="16" /></summary>
        <ul><li v-for="note in invitationRewardNotes" :key="note">{{ note }}</li></ul>
      </details>
    </div>
    <template #footer><button type="button" class="invitation-done activity-action activity-action--secondary" @click="close">我知道了</button></template>
  </ActivityDialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useAppContext } from '../../composables/appContext'
import { INVITATION_REWARD_NOTES, buildInvitationRewardRules } from '../../utils/invitationRewardRules'
import ActivityDialog from '../activities/ActivityDialog.vue'
import SiteIcon from '../SiteIcon.vue'

// 邀请弹窗仅展示本人汇总及公开奖励规则，复用通用弹窗的焦点、Escape和滚动边界。
const { invitationModal, notify } = useAppContext()
const invitationRewardRules = computed(() => buildInvitationRewardRules(invitationModal.value?.rules))
const invitationRewardNotes = INVITATION_REWARD_NOTES
const invitationLink = computed(() => invitationModal.value?.invitationLink || invitationModal.value?.inviteLink || '')
const heroImageFailed = ref(false)
const activityTitle = computed(() => invitationModal.value?.activity?.title || invitationModal.value?.activityTitle || '邀请好友活动')
const heroImage = computed(() => invitationModal.value?.activity?.coverUrl || '')
watch(heroImage, () => { heroImageFailed.value = false })
function close() { invitationModal.value = null }
function formatAmount(value) {
  const amount = Number(value)
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
}
async function copyInvitationLink() {
  if (!invitationLink.value) return
  try {
    await navigator.clipboard.writeText(invitationLink.value)
    notify('邀请链接已复制')
  } catch {
    notify('复制失败，请手动复制邀请链接')
  }
}
</script>

<style>
/* 通用弹窗Teleport至body，所有覆盖限定在邀请弹窗专属类名下。 */
.invitation-backdrop .activity-action-dialog { width:min(720px,calc(100vw - 32px)); max-height:min(760px,calc(100dvh - 48px)); padding:0; overflow:hidden; border-radius:18px; }
.invitation-backdrop .activity-action-dialog > header { margin:0; padding:20px 24px; border-bottom:1px solid #e8edf4; }
.invitation-backdrop .activity-action-dialog > header h2 { font-size:21px; line-height:1.4; }
.invitation-backdrop .activity-dialog-content { padding:0 24px; scrollbar-width:thin; }
.invitation-backdrop .activity-dialog-actions { display:flex; justify-content:flex-end; margin:0; padding:12px 24px; border-top:1px solid #e8edf4; background:#fff; }
.invitation-content { padding:14px 0; color:#34445b; }
.invitation-intro { margin:0 0 14px; color:#718096; font-size:13px; line-height:1.6; }
.invitation-banner { display:block; width:100%; max-height:140px; margin-bottom:18px; border-radius:10px; object-fit:contain; background:#f3f7fc; }
.invitation-summary { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); margin:0 0 16px; padding:16px 0; border:1px solid #e7eef7; border-radius:12px; background:#f4f8fe; }
.invitation-summary > div { min-width:0; padding:0 22px; }
.invitation-summary > div + div { border-left:1px solid #dfe8f3; }
.invitation-summary dt { color:#718096; font-size:12px; line-height:1.5; }
.invitation-summary dd { margin:8px 0 0; color:#293b53; font-size:28px; font-weight:600; line-height:1.2; font-variant-numeric:tabular-nums; overflow-wrap:anywhere; }
.invitation-summary dd span { margin-left:6px; color:#8090a5; font-size:12px; font-weight:400; }
.invitation-summary .reward dd { color:#1687d6; }
.invitation-share { padding-bottom:16px; border-bottom:1px solid #e8edf4; }
.invitation-share-heading { display:flex; align-items:center; justify-content:space-between; gap:16px; }
.invitation-share-heading > div { min-width:0; }
.invitation-share-heading > div > span { display:block; color:#718096; font-size:12px; }
.invitation-share-heading b { display:block; margin-top:4px; color:#34445b; font-size:17px; font-weight:600; overflow-wrap:anywhere; }
.invitation-copy { display:inline-flex; flex:none; align-items:center; justify-content:center; gap:7px; min-height:42px; padding:10px 16px; border:1px solid #1687d6; border-radius:8px; background:#1687d6; color:#fff; font-size:13px; font-weight:600; cursor:pointer; transition:background-color 150ms ease; }
.invitation-copy:hover { background:#1276bd; }
.invitation-copy:focus-visible,.invitation-notes summary:focus-visible { outline:2px solid #1687d6; outline-offset:3px; }
.invitation-link { box-sizing:border-box; display:block; width:100%; min-width:0; height:38px; margin-top:12px; padding:0 12px; border:1px solid #e3eaf3; border-radius:7px; background:#f8fafc; color:#718096; font-family:inherit; font-size:12px; text-overflow:ellipsis; }
.invitation-rules { margin-top:20px; }
.invitation-section-title { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:14px; }
.invitation-section-title h3 { margin:0; color:#34445b; font-size:15px; font-weight:600; line-height:1.5; }
.invitation-section-title > span { color:#8090a5; font-size:11px; }
.invitation-rule-list article { display:flex; align-items:flex-start; gap:12px; padding:16px 0; }
.invitation-rule-list article:first-child { padding-top:0; }
.invitation-rule-list article + article { border-top:1px solid #edf1f6; }
.invitation-rule-number { display:grid; flex:0 0 28px; place-items:center; height:28px; border-radius:7px; background:#edf5ff; color:#2785cf; font-size:11px; font-weight:600; font-variant-numeric:tabular-nums; }
.invitation-rule-list article > div { min-width:0; }
.invitation-rule-list h4 { margin:2px 0 6px; color:#34445b; font-size:14px; font-weight:600; line-height:1.5; overflow-wrap:anywhere; }
.invitation-rule-list p { margin:0 0 7px; color:#718096; font-size:13px; line-height:1.7; overflow-wrap:anywhere; }
.invitation-rule-result { color:#237fbd; font-size:12px; line-height:1.7; overflow-wrap:anywhere; }
.invitation-empty { padding:16px; border:1px dashed #dce5f0; border-radius:10px; background:#fafbfd; }
.invitation-empty b { color:#65738b; font-size:13px; font-weight:500; }
.invitation-empty p { margin:5px 0 0; color:#8793a5; font-size:12px; line-height:1.6; }
.invitation-notes { margin-top:16px; padding-top:12px; border-top:1px solid #e8edf4; }
.invitation-notes summary { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:4px 0; list-style:none; color:#718096; font-size:12px; cursor:pointer; }
.invitation-notes summary::-webkit-details-marker { display:none; }
.invitation-notes[open] summary .site-icon { transform:rotate(180deg); }
.invitation-notes ul { margin:10px 0 0; padding-left:17px; color:#8090a5; font-size:12px; line-height:1.8; }
.invitation-backdrop .activity-dialog-actions button.invitation-done { flex:none; width:auto; min-width:100px; }
@media(max-width:520px) {
  .invitation-backdrop .activity-action-dialog { border-radius:14px; }
  .invitation-backdrop .activity-action-dialog > header { padding:16px; }
  .invitation-backdrop .activity-action-dialog > header h2 { font-size:18px; }
  .invitation-backdrop .activity-dialog-content { padding:0 16px; }
  .invitation-backdrop .activity-dialog-actions { padding:12px 16px; }
  .invitation-summary { padding:16px 0; }
  .invitation-summary > div { padding:0 12px; }
  .invitation-summary dd { font-size:23px; }
  .invitation-share-heading { flex-wrap:wrap; gap:12px; }
  .invitation-copy { min-height:44px; }
  .invitation-section-title { align-items:flex-start; }
}
@media(max-width:360px) {
  .invitation-summary > div { padding:0 10px; }
  .invitation-summary dd { font-size:22px; }
  .invitation-summary dd span { margin-left:3px; font-size:11px; }
  .invitation-copy { width:100%; }
}
@media(prefers-reduced-motion:reduce) { .invitation-copy { transition:none; } }
</style>
